import {
  configKeys,
  ConfigResponseType,
  CONFIGS,
  getConfigEntries,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { UsersSearchResponseType } from '@buildwise/libs/home/components/request-post/RequestPostModal';
import { JAVA_SERVICE_ENDPOINTS } from '@buildwise/libs/services';
import { ArticleSourceType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AxiosResponse } from 'axios';
import Editor, { initConfig } from 'components/common/editor/Editor';
import { getStatusbarStyles } from 'components/common/editor/helpers';
import TinyMceEditorSkelton from 'components/utils/TinyMceEditorSkelton';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useUserData from 'hooks/useUserData';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';

type PostDescriptionProps = {
  autoFocus?: boolean;
  defaultHeight?: number;
  hideToolbar?: boolean;
  isEditing: boolean;
  placeholder?: string;
  showMentions?: boolean;
  source?: string;
  articleType?: ArticleSourceType;
};

function PostDescription({
  autoFocus = false,
  defaultHeight,
  hideToolbar = false,
  isEditing,
  placeholder,
  showMentions = true,
  source,
  articleType = ArticleSourceType.POST,
}: PostDescriptionProps) {
  const {
    control,
    formState: { isSubmitting, errors },
    setValue,
    setFocus,
    watch,
  } = useFormContext<PostFormData>();
  const description = watch('description');
  const queryClient = useQueryClient();
  const { companyId } = useUserData();
  useConfigQuery({
    companyId,
    configName: CONFIGS.HASHTAGS.configName,
  });
  useSearchItemQuery<UsersSearchResponseType>(
    {
      searchType: 'users',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    showMentions,
  );
  const [isLoadingEditor, setLoadingEditor] = useState(true);
  const [showEditor, setShowEditor] = useState(isEditing || !!description || autoFocus);
  const onShowEditor = useCallback(() => {
    setShowEditor(true);
    setFocus('description');
  }, []);

  useEffect(() => {
    if (!showEditor && description) {
      onShowEditor();
    }
  }, [description]);

  const getHashtags = useCallback(async () => {
    const queryData = queryClient.getQueryData<AxiosResponse<ConfigResponseType>>(
      configKeys.getConfig({ companyId, configName: CONFIGS.HASHTAGS.configName }),
    );

    return getConfigEntries({ data: queryData?.data }).map((entry) => entry.entryCode);
  }, [companyId]);

  const getUsers = useCallback(async () => {
    const queryData = queryClient.getQueryData<AxiosResponse<UsersSearchResponseType>>([
      JAVA_SERVICE_ENDPOINTS.SEARCH_ITEM,
      'users',
    ]);

    return (
      queryData?.data?.userResponse?.map((user) => ({
        ...user,
        name: user.userNameToDisplay,
        id: user.username,
        description: user.title,
        image: user?.profileImageURL || 'https://images.buildwise.ai/user_profile_avatar.png',
      })) || []
    );
  }, []);

  return (
    <Box position="relative" mb={3}>
      {!showEditor && (
        <>
          <PlaceholderContainer
            tabIndex={0}
            height={defaultHeight || 250}
            onClick={onShowEditor}
            onFocus={onShowEditor}
          >
            <DescriptionPlaceholder>{placeholder || 'Add a description'}</DescriptionPlaceholder>
          </PlaceholderContainer>
          <FormHelperText error sx={{ mt: -1.5 }}>
            {(errors['description']?.message as string) || ''}
          </FormHelperText>
        </>
      )}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            visibility: showEditor ? 'visible' : 'hidden',
            minHeight: defaultHeight || 250,
            position: showEditor ? 'relative' : 'absolute',
          }}
        >
          <Controller
            control={control}
            render={({ field: { ref, onChange, ...field }, fieldState }) => (
              <FormControl fullWidth>
                <Editor
                  {...field}
                  id="description"
                  onEditorChange={onChange}
                  onInit={(_, editor) => {
                    ref(editor);
                    // editor.editorContainer.style.transition = 'height 0.3s';
                    setLoadingEditor(false);
                  }}
                  value={!!field?.value?.trim() ? field.value : ''}
                  disabled={isSubmitting}
                  init={{
                    placeholder: placeholder || 'Add a description',
                    min_height: defaultHeight || 250,
                    height: defaultHeight || 250,
                    ...getStatusbarStyles(articleType),
                    auto_focus: autoFocus ? true : undefined,
                    ...(source === 'convert_to_post' || hideToolbar
                      ? { toolbar: false }
                      : {
                          toolbar: `${initConfig()?.toolbar ?? ''} emoticons`,
                        }),
                    plugins: [...(initConfig()?.plugins || []), 'autoresize', 'emoticons'],
                  }}
                  onBlur={() => {
                    field.onBlur();
                    // Clear Write with AI state when user removes last generated AI summary
                    if (!field?.value?.trim()) {
                      setValue('isWritingWithAI', false);
                    }
                    setValue('description', field?.value?.trim() || '');
                  }}
                  getHashtags={getHashtags}
                  {...(showMentions && { getUsers })}
                />
                <FormHelperText error>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
            name="description"
          />
          {isLoadingEditor && <TinyMceEditorSkelton source={source} />}
        </Box>
      </Box>
    </Box>
  );
}

const PlaceholderContainer = styled(Typography)(({ theme }) => ({
  cursor: 'text',
  padding: '6px 8px',
  transition: 'background-color 0.3s',
  backgroundColor: theme.palette.custom.whiteSmoke,
  borderRadius: '4px',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'flex-start',
  '&:hover': {
    backgroundColor: theme.palette.custom.inputHoverBackground,
  },
}));

const DescriptionPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.custom.coolGrey,
}));

export default PostDescription;
